import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonSpinner,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';

import { DeleteZoneMessage } from '../../../../../common/enums/delete-zone-message.enum';
import { StorageLabel } from '../../../../../common/enums/storage-label.enum';
import { LogFactory } from '../../../../../common/services/logger/log-factory';
import { describeItem, ITEM_TYPE_LABEL, ItemType } from '../../models/item-type';
import { CloudItem, CloudItemDetail } from '../../sync/api-sync.backend';
import { SyncService } from '../../sync/sync.service';
import { DbService } from '../../services/db.service';
import { formatBytes } from '../../utils/format-bytes.util';

/**
 * Read-only preview of one cloud item, opened by tapping a row in Cloud Delete.
 *
 * Deliberately built from CLOUD data, not the local library: the cloud can hold
 * content no current device has a copy of — that is precisely the content a user
 * is most likely to be surprised by and most willing to delete — and a preview
 * that only worked for items already on the phone would be silent about exactly
 * those. The cover image is the one part that falls back to the local blob, since
 * fetching art for something about to be destroyed is not worth the round trip.
 *
 * It shows what the item contains and what deleting it frees, then offers the
 * same delete the list does; the host owns the confirm, so the wording of an
 * irreversible action is written in one place.
 */
@Component({
  selector: 'app-cloud-item-preview',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonSpinner,
    IonTitle,
    IonToolbar,
  ],
  template: `
    <ion-header>
      <ion-toolbar>
        <ion-title>{{ summary.name || 'Untitled' }}</ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="close()" aria-label="Close">
            <ion-icon slot="icon-only" name="close"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content class="ion-padding">
      <div class="head">
        @if (coverUrl(); as url) {
          <img class="cover" [src]="url" [alt]="summary.name || 'Untitled'" />
        }
        <div>
          <div class="kind">{{ kindLabel }} · {{ formatBytes(summary.totalBytes) }}</div>
          <div class="frees" data-testid="cp-frees">{{ freesExplanation }}</div>
        </div>
      </div>

      @if (loading()) {
        <div class="state-block"><ion-spinner name="crescent"></ion-spinner></div>
      } @else if (loadError()) {
        <p class="muted" data-testid="cp-load-error">{{ CloudLoadFailed }}</p>
      } @else if (detail(); as d) {
        @if (d.blocked) {
          <p class="blocked-note" data-testid="cp-blocked">{{ usedInLabel(d) }}</p>
        }
        <!-- Never optional-chained: getCloudItem defaults contents to [] at the
             boundary, so the type's guarantee holds here. -->
        @if (d.contents.length) {
          <div class="section-title"><h2>{{ ContentsHeading }}</h2></div>
          <div class="contents surface">
            @for (child of d.contents; track child.kind + ':' + child.id) {
              <ion-item lines="none" data-testid="cp-content">
                <ion-label>
                  <div class="child-name">{{ child.name || 'Untitled' }}</div>
                  <p class="muted">{{ kindLabelOf(child.kind) }} · {{ formatBytes(child.totalBytes) }}</p>
                </ion-label>
              </ion-item>
            }
          </div>
        }
      }
    </ion-content>

    <ion-toolbar class="actions">
      <ion-button slot="start" fill="clear" (click)="close()">{{ CloseButton }}</ion-button>
      <ion-button
        slot="end"
        color="danger"
        [disabled]="detail()?.blocked ?? summary.blocked"
        (click)="requestDelete()"
        data-testid="cp-delete"
      >
        {{ DeleteForeverButton }}
      </ion-button>
    </ion-toolbar>
  `,
  styles: [`
    .head { display: flex; gap: 14px; align-items: center; margin-bottom: 18px; }
    .cover { width: 72px; height: 72px; border-radius: 10px; object-fit: cover; flex: none; }
    .kind { font-weight: 700; letter-spacing: -0.02em; }
    .frees { color: var(--ion-color-medium); font-size: 0.9rem; margin-top: 2px; }
    .blocked-note { color: var(--ion-color-warning-shade); font-size: 0.9rem; }
    .state-block { text-align: center; padding: 24px; }
    .contents { overflow: hidden; border-radius: 12px; }
    .contents > ion-item { --background: transparent; }
    .contents > ion-item + ion-item { border-top: 1px solid var(--app-card-border, var(--app-border)); }
    .child-name { font-weight: 600; }
    .muted { color: var(--ion-color-medium); font-size: 0.85rem; }
    .actions { --background: var(--ion-background-color); }
  `],
})
export class CloudItemPreviewComponent implements OnInit, OnDestroy {
  private readonly sync = inject(SyncService);
  private readonly db = inject(DbService);
  private readonly modalCtrl = inject(ModalController);
  private readonly logger = inject(LogFactory).createLogger(CloudItemPreviewComponent);

  // Plain @Input(), NOT signal inputs: ModalController delivers componentProps by
  // assigning them onto the instance, which overwrites an InputSignal with a raw
  // value (the template then calls a non-function) and lands after construction
  // (a required signal input read in the constructor throws NG0950).
  @Input() kind!: ItemType;
  @Input() itemId!: string;
  /** The list row already has name/sizes — render immediately, fill in contents. */
  @Input() summary!: CloudItem;

  protected readonly ContentsHeading = DeleteZoneMessage.CloudContentsHeading;
  protected readonly CloudLoadFailed = DeleteZoneMessage.CloudLoadFailed;
  protected readonly CloseButton = DeleteZoneMessage.CloseButton;
  protected readonly DeleteForeverButton = DeleteZoneMessage.DeleteForeverButton;
  protected readonly formatBytes = formatBytes;

  protected readonly detail = signal<CloudItemDetail | null>(null);
  protected readonly loading = signal(true);
  protected readonly loadError = signal(false);
  protected readonly coverUrl = signal<string | null>(null);

  protected get kindLabel(): string {
    return this.kindLabelOf(this.kind);
  }

  /**
   * Why the reclaim figure is what it is — the sheet has room for the sentence
   * the row cannot fit.
   *
   * The case that needs it most is zero. An item plainly holding 42 MB whose
   * delete frees nothing looks broken, and "Frees 0 B" is no help. It is neither
   * broken nor a dead end: every blob is held by something else too, and the
   * space comes back when the last holder goes. Saying only the first half would
   * leave the user believing the space is unreclaimable.
   */
  protected get freesExplanation(): string {
    const exclusive = this.summary.exclusiveBytes;
    const shared = Math.max(0, this.summary.totalBytes - exclusive);
    if (exclusive === 0 && shared > 0) {
      return (
        `${StorageLabel.FreesNothingAlone} — all ${formatBytes(shared)} is ` +
        `${StorageLabel.SharedMedia}. ${StorageLabel.SharedFreedByLast}`
      );
    }
    const frees = `${StorageLabel.Frees} ${formatBytes(exclusive)} ${DeleteZoneMessage.CloudFreesIfDeleted}`;
    if (shared === 0) return frees;
    return (
      `${frees}. The other ${formatBytes(shared)} is ${StorageLabel.SharedMedia}, ` +
      `${StorageLabel.SharedStays}.`
    );
  }

  /** Loads in ngOnInit, not the constructor: componentProps are assigned after
   *  construction, so there is nothing to load from yet. */
  ngOnInit(): void {
    void this.load();
  }

  ngOnDestroy(): void {
    const url = this.coverUrl();
    if (url) URL.revokeObjectURL(url);
  }

  protected kindLabelOf(kind: ItemType): string {
    return ITEM_TYPE_LABEL[kind];
  }

  /** `used in group "Leg Day" (+2 more)` — why the delete button is disabled.
   *  The kind is named and the name quoted because item names read as anything:
   *  a program called "2 days" makes "used in 2 days" a deadline.
   *
   *  `usages` is capped for display, so the remainder comes from `usageTotal`
   *  rather than from the array's length. */
  protected usedInLabel(item: CloudItem): string {
    const described = item.usages.map((u) => describeItem(u.kind, u.name));
    if (!described.length) return StorageLabel.UsedElsewhere;
    const more = Math.max(0, item.usageTotal - described.length);
    return `${StorageLabel.UsedIn} ${described.join(', ')}${more ? ` (+${more} more)` : ''}`;
  }

  protected close(): void {
    void this.modalCtrl.dismiss();
  }

  protected requestDelete(): void {
    // The host owns the confirm so the wording of an irreversible delete lives in
    // one place, whether it was started from a row or from here.
    void this.modalCtrl.dismiss({ deleteRequested: true });
  }

  private async load(): Promise<void> {
    void this.loadCover();
    try {
      this.detail.set(await this.sync.getCloudItem(this.kind, this.itemId));
    } catch (err) {
      this.logger.error('Loading the Cloud item preview failed', err, {
        kind: this.kind,
        itemId: this.itemId,
      });
      this.loadError.set(true);
    } finally {
      this.loading.set(false);
    }
  }

  /** Local blob only: an item with no device copy simply shows no cover. */
  private async loadCover(): Promise<void> {
    const mediaId = this.summary?.imageMediaId;
    if (!mediaId) return;
    const blob = await this.db.getMedia(mediaId);
    if (blob) this.coverUrl.set(URL.createObjectURL(blob));
  }
}
